/**
 * Generic conversion engine backed by the runtime's own ICU calendar data.
 *
 * Both non-Gregorian calendars we need are *astronomical*: the Solar Hijri
 * (Persian) year begins at the true vernal equinox and the Umm al-Qura year
 * is derived from observed lunar visibility. Neither has a closed-form
 * arithmetic rule - in particular the Solar Hijri leap cycle is not periodic
 * with 33 years, so any hand-rolled table eventually drifts.
 *
 * Every runtime we target (Chrome, Electron, Node, Cloudflare Workers) ships
 * full ICU, so we delegate the hard part to `Intl.DateTimeFormat` and only
 * supply the JDN <-> calendar plumbing ourselves.
 */

export const JDN_UNIX_EPOCH = 2440588; // 1970-01-01

export function jdnToMs(jdn: number): number {
  return (jdn - JDN_UNIX_EPOCH) * 86400000;
}

export function msToJdn(ms: number): number {
  return Math.floor(ms / 86400000) + JDN_UNIX_EPOCH;
}

/** Inclusive JDN bounds the engine is willing to search. */
const JDN_SEARCH_MIN = 2000000; // ~ 764 CE
const JDN_SEARCH_MAX = 2600000; // ~ 2555 CE

export interface IcuDate {
  year: number;
  month: number;
  day: number;
}

/**
 * Verify the host runtime can actually compute `calendar`, returning false
 * for small-ICU builds that silently fall back to the Gregorian calendar.
 */
export function supportsCalendar(calendar: string): boolean {
  try {
    const d = new Date(Date.UTC(2024, 5, 16));
    const parts = new Intl.DateTimeFormat("en-u-ca-" + calendar, {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      timeZone: "UTC",
    }).formatToParts(d);
    // A Persian year must be 4 digits and far from a Gregorian one; an
    // unsupported calendar is echoed back verbatim as the Gregorian year.
    const year = Number(parts.find((p) => p.type === "year")?.value);
    return Number.isInteger(year) && year > 1000;
  } catch {
    return false;
  }
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function getFormatter(calendar: string): Intl.DateTimeFormat {
  let fmt = formatterCache.get(calendar);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat("en-u-ca-" + calendar, {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      timeZone: "UTC",
    });
    formatterCache.set(calendar, fmt);
  }
  return fmt;
}

/** Read an ICU calendar date out of a Julian Day Number. */
export function icuJdnTo(jdn: number, calendar: string): IcuDate {
  if (jdn < JDN_SEARCH_MIN || jdn > JDN_SEARCH_MAX) {
    throw new RangeError(
      `Julian day ${jdn} is outside the supported range of the ${calendar} calendar`,
    );
  }
  const parts = getFormatter(calendar).formatToParts(
    new Date(jdnToMs(jdn)),
  );
  let year = 0;
  let month = 0;
  let day = 0;
  for (const p of parts) {
    if (p.type === "year") year = Number(p.value);
    else if (p.type === "month") month = Number(p.value);
    else if (p.type === "day") day = Number(p.value);
  }
  if (!year || !month || !day) {
    throw new Error(
      `Could not read a ${calendar} date for JDN ${jdn}; this runtime may lack full ICU`,
    );
  }
  return { year, month, day };
}

const ordinal = (d: IcuDate): number => d.year * 10000 + d.month * 100 + d.day;

const jdnOrdinalCache = new Map<string, (jdn: number) => number>();

function jdnOrdinal(calendar: string): (jdn: number) => number {
  let fn = jdnOrdinalCache.get(calendar);
  if (!fn) {
    fn = (jdn: number) => ordinal(icuJdnTo(jdn, calendar));
    jdnOrdinalCache.set(calendar, fn);
  }
  return fn;
}

/**
 * Inverse of {@link icuJdnTo}. Calendar dates increase monotonically with the
 * Julian Day Number, so an exact binary search recovers the authoritative JDN
 * without shipping (or hand-maintaining) a month-length table.
 */
export function icuToJdn(date: IcuDate, calendar: string): number {
  const target = ordinal(date);
  const at = jdnOrdinal(calendar);
  let lo = JDN_SEARCH_MIN;
  let hi = JDN_SEARCH_MAX;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (at(mid) < target) lo = mid + 1;
    else hi = mid;
  }
  if (at(lo) !== target) {
    throw new RangeError(
      `${date.year}-${date.month}-${date.day} is not a valid ${calendar} date`,
    );
  }
  return lo;
}

export function makeCalendarEngine(calendar: string) {
  return {
    name: calendar,
    toJdn: (d: IcuDate) => icuToJdn(d, calendar),
    fromJdn: (jdn: number) => icuJdnTo(jdn, calendar),
  };
}
